import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { createClient } from '@supabase/supabase-js'
import { supabase, supabaseAdmin } from '../lib/supabase'
import type { Database } from '../lib/supabase'
import { useAuth } from './AuthContext'
import type { Tables } from '../lib/database.types'

type AgencyConfig = Tables<'agency_configs'> & {
    agency_name?: string | null;
    logo_url?: string | null;
    mortgage_default_interest?: number | null;
    mortgage_default_years?: number | null;
    portal_idealista_url?: string | null;
    portal_pisos_url?: string | null;
    portal_yaencontre_url?: string | null;
    banners_list?: any;
    opening_hours_es?: string | null;
    opening_hours_ca?: string | null;
    agency_website_url?: string | null;
    secondary_language?: string | null;
}

interface ThemeContextValue {
    config: AgencyConfig | null
    isLoading: boolean
    refresh: () => Promise<void>
    updateConfig: (key: string, value: any) => Promise<void>
}

const ThemeContext = createContext<ThemeContextValue>({ 
    config: null, 
    isLoading: true, 
    refresh: async () => {},
    updateConfig: async () => {} 
})

export function useTheme() {
    return useContext(ThemeContext)
}

interface ThemeProviderProps {
    agencySlug: string
    children: React.ReactNode
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCESSIBILITY UTILS (Sincronizado con PublicHome)
// ─────────────────────────────────────────────────────────────────────────────

function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
}

function getLuminance(r: number, g: number, b: number) {
    const a = [r, g, b].map(v => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function getContrast(rgb1: any, rgb2: any) {
    const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
    const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
}

function darkenColor(hex: string, amount: number) {
    const rgb = hexToRgb(hex);
    const r = Math.max(0, Math.floor(rgb.r * (1 - amount)));
    const g = Math.max(0, Math.floor(rgb.g * (1 - amount)));
    const b = Math.max(0, Math.floor(rgb.b * (1 - amount)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function ensureContrast(color: string, bgColor: string = '#ffffff', minRatio: number = 4.5): string {
    if (!color || !color.startsWith('#')) return color;
    let current = color;
    const bgRgb = hexToRgb(bgColor);
    let safety = 0;
    while (getContrast(hexToRgb(current), bgRgb) < minRatio && safety < 10) {
        current = darkenColor(current, 0.1);
        safety++;
    }
    return current;
}

/** Aplica las variables CSS del tema al :root */
function applyTheme(config: AgencyConfig) {
    try {
        const root = document.documentElement
        
        // Validar que los colores son strings antes de procesar
        const rawPrimary = typeof config.primary_color === 'string' ? config.primary_color : '#0f172a'
        const rawAccent = typeof config.accent_color === 'string' ? config.accent_color : '#2563eb'

        // Garantizar accesibilidad básica
        const primary = ensureContrast(rawPrimary, '#ffffff', 4.5)
        const accent = ensureContrast(rawAccent, '#ffffff', 4.5)
        
        root.style.setProperty('--color-primary', primary)
        root.style.setProperty('--color-accent', accent)
        root.style.setProperty('--color-secondary', typeof config.secondary_color === 'string' ? config.secondary_color : '#f5f5f5')
        
        const hPrimary = (config as any).hover_primary_color
        const hAccent = (config as any).hover_accent_color
        root.style.setProperty('--color-hover-primary', typeof hPrimary === 'string' ? hPrimary : darkenColor(primary, 0.15))
        root.style.setProperty('--color-hover-accent', typeof hAccent === 'string' ? hAccent : darkenColor(accent, 0.15))
        
        const iColor = (config as any).icon_color
        const hiColor = (config as any).hover_icon_color
        root.style.setProperty('--color-icon', typeof iColor === 'string' ? iColor : accent)
        root.style.setProperty('--color-hover-icon', typeof hiColor === 'string' ? hiColor : darkenColor(accent, 0.2))
        
        root.style.setProperty('--border-radius', typeof config.border_radius === 'string' ? config.border_radius : '10px')
        root.style.setProperty('--font-family', typeof config.font_family === 'string' ? config.font_family : 'Inter, sans-serif')
        
        // Sidebar Admin con contraste garantizado contra su fondo
        const sidebarBg = typeof (config as any).admin_sidebar_bg === 'string' ? (config as any).admin_sidebar_bg : '#0f172a'
        const sidebarText = typeof (config as any).admin_sidebar_text === 'string' ? (config as any).admin_sidebar_text : '#ffffff'
        root.style.setProperty('--admin-sidebar-bg', sidebarBg)
        root.style.setProperty('--admin-sidebar-text', ensureContrast(sidebarText, sidebarBg, 4.5))

        // Calcular variantes de color (lighten/darken aproximados via opacity)
        root.style.setProperty('--color-primary-10', primary + '1a')
        root.style.setProperty('--color-accent-10', accent + '1a')

        // Favicon & Apple Touch Icon
        if (config.favicon_url && typeof config.favicon_url === 'string') {
            const rels = ['icon', 'shortcut icon', 'apple-touch-icon']
            rels.forEach(rel => {
                let link = document.querySelector(`link[rel*="${rel}"]`) as HTMLLinkElement
                if (!link) {
                    link = document.createElement('link')
                    link.rel = rel
                    document.head.appendChild(link)
                }
                link.href = config.favicon_url
            })
        }

        // Título de la página
        const agencyName = config.agency_name || 'InmoCRM'
        if (document.title !== agencyName) {
            document.title = agencyName
        }
    } catch (e) {
        console.error('CRITICAL: applyTheme failed:', e)
    }
}

export function ThemeProvider({ agencySlug, children }: ThemeProviderProps) {
    const { profile } = useAuth()
    const [config, setConfig] = React.useState<AgencyConfig | null>(null)
    const [isLoading, setIsLoading] = React.useState(true)

    const loadTheme = async (silent = false) => {
        if (!silent && !config) setIsLoading(true)
        try {
            let targetAgencyId: string | null = null

            // Modo Sistema / SuperAdmin
            if (agencySlug === 'system') {
                try {
                    // Obtener cliente admin con soporte para service key override
                    const override = localStorage.getItem('override_service_key')
                    const adminCli = (override && override.length > 20)
                        ? createClient<Database>(import.meta.env.VITE_SUPABASE_URL as string, override, {
                            auth: { persistSession: false, autoRefreshToken: false }
                          })
                        : supabaseAdmin

                    // Buscar la agencia de sistema en BBDD
                    const { data: agency } = await supabase
                        .from('agencies')
                        .select('id')
                        .eq('slug', 'system')
                        .maybeSingle()
                    
                    if (agency) {
                        const { data: cfg } = await adminCli
                            .from('agency_configs')
                            .select('*')
                            .eq('agency_id', agency.id)
                            .maybeSingle()
                        
                        if (cfg) {
                            const mergedCfg = {
                                ...cfg,
                                agency_name: cfg.agency_name || 'InmoCRM SuperAdmin'
                            }
                            setConfig(mergedCfg as any)
                            applyTheme(mergedCfg as any)
                            setIsLoading(false)
                            return
                        }
                    }
                } catch (e) {
                    console.warn('ThemeProvider: failed to fetch system configs from DB, using fallback defaults', e)
                }

                // Fallback a valores por defecto si no existe en BBDD o si falla la carga
                const systemCfg: AgencyConfig = {
                    agency_id: 'system',
                    primary_color: '#0f172a',
                    accent_color: '#4f46e5',
                    admin_sidebar_bg: '#0f172a',
                    admin_sidebar_text: '#ffffff',
                    agency_name: 'InmoCRM SuperAdmin',
                    banners_list: [],
                    portals_list: [],
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                } as any
                setConfig(systemCfg)
                applyTheme(systemCfg)
                setIsLoading(false)
                return
            }

            // Priorizar el slug de la URL (si existe) para permitir multi-tenant reactivo
            if (agencySlug && agencySlug !== 'system') {
                const { data: agency, error: agencyError } = await supabase
                    .from('agencies')
                    .select('id')
                    .eq('slug', agencySlug)
                    .eq('is_active', true)
                    .maybeSingle()

                if (!agencyError && agency) targetAgencyId = agency.id
                else if (agencyError) console.warn('ThemeProvider: Agency lookup error:', agencyError)
            }

            // Si no hay slug o falló la búsqueda, usamos el de la sesión
            if (!targetAgencyId && profile?.agency_id) {
                targetAgencyId = profile.agency_id
            }

            if (!targetAgencyId) {
                console.warn('ThemeProvider: No target agency resolved.')
                setIsLoading(false)
                return
            }

            const client = profile?.role === 'superadmin' ? supabaseAdmin : supabase;
            
            const { data: cfg, error: cfgError } = await client
                .from('agency_configs')
                .select('*')
                .eq('agency_id', targetAgencyId)
                .single()

            if (cfgError) {
                console.warn('ThemeProvider: Fetch error or missing config:', cfgError)
                
                // Fetch basic agency data for robust fallback
                const { data: agencyData } = await client.from('agencies').select('name, slug, email, phone').eq('id', targetAgencyId).maybeSingle()
                const fallbackName = agencyData?.name || (agencySlug && agencySlug !== 'system' ? agencySlug.charAt(0).toUpperCase() + agencySlug.slice(1) : 'Mi Inmobiliaria')

                const fallbackCfg: AgencyConfig = {
                    agency_id: targetAgencyId,
                    primary_color: '#0f172a',
                    accent_color: '#3b82f6',
                    agency_name: fallbackName,
                    banners_list: [],
                    portals_list: [],
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                } as any

                // Si es un error de "no encontrado", intentamos crear uno por defecto (si es admin)
                if (cfgError.code === 'PGRST116') {
                    try {
                        const { data: created } = await client.from('agency_configs').insert({
                            agency_id: targetAgencyId,
                            primary_color: '#0f172a',
                            accent_color: '#3b82f6',
                            agency_name: fallbackName
                        }).select().single()
                        if (created) {
                            setConfig(created as any)
                            applyTheme(created as any)
                            return
                        }
                    } catch (e) {
                         console.error('Failed to create default config:', e)
                    }
                }
                
                setConfig(fallbackCfg)
                applyTheme(fallbackCfg)
            } else if (cfg) {
                // Identity Patching for existing but incomplete configs
                const finalCfg = { ...cfg, secondary_language: cfg.secondary_language || 'ca' }

                // Asegurar parsing de listas JSON
                if (typeof finalCfg.banners_list === 'string') {
                    try { finalCfg.banners_list = JSON.parse(finalCfg.banners_list); } catch (e) { finalCfg.banners_list = []; }
                }
                if (typeof finalCfg.portals_list === 'string') {
                    try { finalCfg.portals_list = JSON.parse(finalCfg.portals_list); } catch (e) { finalCfg.portals_list = []; }
                }
                
                if (!finalCfg.agency_name || finalCfg.agency_name === 'Mi Inmobiliaria') {
                    const { data: agencyData } = await client.from('agencies').select('name, slug').eq('id', targetAgencyId).maybeSingle()
                    if (agencyData) {
                        finalCfg.agency_name = agencyData.name || (agencyData.slug ? agencyData.slug.charAt(0).toUpperCase() + agencyData.slug.slice(1) : 'InmoCRM')
                    }
                }

                setConfig(finalCfg as any)
                applyTheme(finalCfg as any)

                // Inyectar CSS personalizado si existe
                const existing = document.getElementById('agency-custom-css')
                if (existing) existing.remove()
                
                if (cfg.custom_css) {
                    const style = document.createElement('style')
                    style.id = 'agency-custom-css'
                    style.textContent = cfg.custom_css
                    document.head.appendChild(style)
                }
            }
        } catch (e) {
            console.error('ThemeProvider error:', e)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        loadTheme()
        return () => {
            document.getElementById('agency-custom-css')?.remove()
        }
    }, [agencySlug, profile?.agency_id])

    const updateConfig = async (key: string, value: any) => {
        if (!config?.agency_id) return
        try {
            const { error } = await supabase
                .from('agency_configs')
                .update({ [key]: value })
                .eq('agency_id', config.agency_id)
            
            if (error) throw error
            
            // Actualización local optimista
            const newCfg = { ...config, [key]: value }
            setConfig(newCfg as any)
            applyTheme(newCfg as any)
        } catch (e) {
            console.error('Failed to update config:', e)
        }
    }

    const value = useMemo(() => ({ 
        config, 
        isLoading, 
        refresh: loadTheme,
        updateConfig 
    }), [config, isLoading])

    return (
        <ThemeContext.Provider value={value}>
            {children}
            {isLoading && !config && (
                <div className="app-loading">
                    <div className="spinner" />
                </div>
            )}
        </ThemeContext.Provider>
    )
}
